import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ProbabilitySection from './probabilitySection';

const safeNumber = (value, defaultValue = 0) => {
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }
  const parsed = parseFloat(value);
  return !isNaN(parsed) ? parsed : defaultValue;
};

const formatPercentage = (value) => {
  const num = safeNumber(value);
  return Math.round(num);
};

/**
 * HoverTooltip - Component for displaying image tooltip on hover
 */
const HoverTooltip = ({ children, imageUrl, highlightText }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e) => {
    setIsHovered(true);
    const rect = e.target.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <>
      <span
        className="bg-yellow-100 px-1 rounded cursor-pointer hover:bg-yellow-200 transition-colors duration-200"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {highlightText}
      </span>
      {isHovered && (
        <div
          className="fixed z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-2"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translate(-50%, -100%)',
            maxWidth: '300px'
          }}
        >
          <img
            src={imageUrl}
            alt="Parts Shop game"
            className="w-full h-auto rounded"
            style={{ maxHeight: '200px', objectFit: 'contain' }}
          />
        </div>
      )}
      {children}
    </>
  );
};

/**
 * ProblemSolvingSection - Specialized component for displaying problem solving construct data
 *
 * This component provides a unique UI for the Problem Solving construct, featuring:
 * - Left side: Existing probability section (unchanged)
 * - Right side: Personalized passages with insights based on the user's category and 5 specific behavioral insights
 *
 * The component safely handles data extraction from Redux contextData and provides
 * fallback values for missing or invalid data.
 *
 * @returns {JSX.Element} The problem solving dashboard with probability chart and personalized content
 */
const ProblemSolvingSection = () => {
  const { contextData } = useSelector((state) => state.studentPerformenceConstruct);

  const problemSolvingData = useMemo(() => {
    if (!contextData) return null;

    const { construct_label = [] } = contextData;

    const efficientLabel = construct_label.find(item =>
      item.behavior_graph_key?.toLowerCase().includes('problemsolving') &&
      item.behavior_graph_key?.toLowerCase().includes('efficient')
    );

    const creativeLabel = construct_label.find(item =>
      item.behavior_graph_key?.toLowerCase().includes('problemsolving') &&
      item.behavior_graph_key?.toLowerCase().includes('creative')
    );

    const emergingLabel = construct_label.find(item =>
      item.behavior_graph_key?.toLowerCase().includes('problemsolving') &&
      item.behavior_graph_key?.toLowerCase().includes('emerging')
    );

    if (!efficientLabel && !creativeLabel && !emergingLabel) {
      return null; 
    }

    const categories = [];

    if (efficientLabel && typeof efficientLabel.percentage !== 'undefined') {
      const efficientPercentage = Math.max(0, Math.min(100, safeNumber(efficientLabel.percentage)));
      categories.push({ name: 'Efficient', percentage: efficientPercentage, color: '#22c55e' });
    }

    if (creativeLabel && typeof creativeLabel.percentage !== 'undefined') {
      const creativePercentage = Math.max(0, Math.min(100, safeNumber(creativeLabel.percentage)));
      categories.push({ name: 'Creative', percentage: creativePercentage, color: '#f59e0b' });
    }

    if (emergingLabel && typeof emergingLabel.percentage !== 'undefined') {
      const emergingPercentage = Math.max(0, Math.min(100, safeNumber(emergingLabel.percentage)));
      categories.push({ name: 'Emerging', percentage: emergingPercentage, color: '#f97316' });
    }

    if (categories.length === 0) {
      return null; 
    }

    const dominantCategory = categories.reduce((prev, current) =>
      (prev.percentage > current.percentage) ? prev : current
    );

    return {
      dominantCategory
    };
  }, [contextData]);

  const getPassageContent = (dominantCategory) => {
    switch (dominantCategory.name) {
      case 'Efficient':
        return {
          category: 'Efficient Problem Solvers',
          color: '#22c55e',
          gamePassage: `You play like a clean mechanic: scan the parts list, pop the cover, and snap pieces into place with barely a wasted click. By revealing hidden drops early you erase guesswork, then string together high-value matches in tight, efficient chains. Each level feels less like a puzzle and more like a well-oiled assembly—quick checks, precise moves, predictable results.`,
          generalPassage: `In day-to-day problem solving you favour clarity and economy. You gather key facts up front, map the shortest workable path, and execute with little detour. Because you minimise uncertainty early, you rarely need major course corrections, freeing mental space for refinement rather than repair.`
        };
      case 'Creative':
        return {
          category: 'Creative Problem Solvers',
          color: '#f59e0b',
          gamePassage: (
            <>
              <HoverTooltip
                imageUrl="https://fe-staging.censioanalytics.com/static/media/great-escape-2-0.72dd380a5fa3a3e3d841.png"
                highlightText="In the Parts Shop game"
              />, you treat the grid like a sketch pad—rotating, stashing, and re-using pieces until a novel pattern pops. A few extra moves are part of the plan; each trial adds a brushstroke that sharpens the final image. The thrill comes from that "aha" moment when an unlikely combo clears the board and bumps the score, proving that curiosity pays off.
            </>
          ),
          generalPassage: `When tackling open-ended tasks you roam widely, sampling half-formed ideas and recombining scraps others might overlook. Iteration is your engine: every draft, detour, or misfit component can spark the breakthrough. Because you balance playfulness with follow-through, your inventive starts often mature into polished, workable results.`
        };
      case 'Emerging':
        return {
          category: 'Emerging Problem Solvers',
          color: '#f97316',
          gamePassage: `You're in exploration mode—dropping pieces, watching reactions, and learning which patterns click. Some rounds end in spectacular chains; others stall when a hidden block derails the plan. Each experiment, successful or not, adds a data point, and every replay sharpens your sense of what works on the grid.`,
          generalPassage: `In everyday problem situations you're building your toolkit. You dive in, try approaches, and gauge the outcome—sometimes hitting a breakthrough, other times circling back for a fresh angle. The variability is part of growth: each attempt teaches a nuance about constraints and levers. As you pause more often to scan the landscape before acting, your hit rate will climb and those big wins will become more consistent.`
        };
      default:
        return {
          category: 'Problem Solving Analysis',
          color: '#6b7280',
          gamePassage: 'Analysis of your problem solving patterns is being processed.',
          generalPassage: 'Analysis of your problem solving patterns is being processed.'
        };
    }
  };

  const getBehavioralInsights = (dominantCategory) => {
    switch (dominantCategory.name) {
      case 'Efficient':
        return [
          {
            title: 'Pattern Recognition',
            description: 'You spot useful patterns quickly and turn them into matches with minimal trial-and-error.'
          },
          {
            title: 'Early Information Gathering',
            description: 'When information is hidden you reveal it early, so you\'re rarely surprised later in the round.'
          },
          {
            title: 'Streamlined Solutions',
            description: 'You keep move counts lean, solving most orders in a streamlined sequence.'
          }
        ];
      case 'Creative':
        return [
          {
            title: 'Resourceful Experimentation',
            description: 'You experiment with "discarded" pieces, turning recycled parts into unexpected wins.'
          },
          {
            title: 'Exploratory Approach',
            description: 'Your solutions unfold in longer, exploratory chains that test several ideas before locking one in.'
          },
          {
            title: 'Successful Innovation',
            description: 'Even with the extra experimentation, you finish a striking share of orders successfully.'
          }
        ];
      case 'Emerging':
        return [
          {
            title: 'Growing Efficiency',
            description: 'You clear some matches but the ratio of success to effort is still growing.'
          },
          {
            title: 'Quick Decision Making',
            description: 'You move quickly through turns, sometimes committing before all options are visible.'
          },
          {
            title: 'Variable Performance',
            description: 'Scores swing high and low as you test different tactics to see what sticks.'
          }
        ];
      default:
        return [];
    }
  };

  if (!problemSolvingData) {
    return (
      <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading problem solving data...</p>
        </div>
      </div>
    );
  }

  const { dominantCategory } = problemSolvingData;
  const passageInfo = getPassageContent(dominantCategory);

  return (
    <div>
      {/* Keep existing Probability Section layout - DO NOT CHANGE */}
      <div className="flex flex-col md:flex-row">
        <div className="w-full md:w-1/2 bg-blue-50 rounded-l-lg overflow-hidden">
          {/* Use the existing ProbabilitySection component - keeping it unchanged */}
          <ProbabilitySection />

          {/* Explanatory text below the chart */}

          <div className="px-8 py-6 border-t border-blue-100 rounded-br-lg rounded-bl-lg">
            <p className="text-sm sm:text-md md:text-lg text-gray-700 leading-relaxed">
              The probabilities for this model represent the confidence of the model that you belong with this overall group. These probabilities range from 0% (you are very unlikely to belong to the group) to 100% (you are very likely to belong to the group). Predictions have been scaled to sum to 100%, so if you receive scores of 40% for two groups, you are equally likely to be in either — the model isn’t sure which to pick between the two options.
            </p>
          </div>
        </div>

        {/* Right side - Personalized Passage */}
        <div className="w-full md:w-1/2 bg-white p-6 rounded-r-lg border-t border-r border-b border-gray-200 shadow-sm">
          <div className="flex items-center mb-4">
            <div className="w-5 h-5 rounded mr-3" style={{ backgroundColor: passageInfo.color }}></div>
            <h3 className="text-lg sm:text-xl md:text-2xl font-semibold">{passageInfo.category}</h3>
          </div>

          {/* Your performance in the game */}
          <div className="mb-6">
            <h4 className="text-md sm:text-lg md:text-xl font-semibold text-gray-900 mb-3 pb-2">Your performance in the game :</h4>
            <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-xl break-words">
              {passageInfo.gamePassage}
            </p>
          </div>

          {/* Your Problem Solving Style */}
          <div className="mb-6">
            <h4 className="text-md sm:text-lg md:text-xl font-semibold text-gray-900 mb-3 pb-2">Your Problem Solving Style :</h4>
            <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-xl break-words">
              {passageInfo.generalPassage}
            </p>
          </div>

          {/* Behavioral Observations */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3 pb-4">Behavioral observations :</h4>
            <div className="space-y-3">
              {getBehavioralInsights(dominantCategory).map((insight, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-gray-400 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <p className="text-xs sm:text-sm md:text-lg text-gray-700 leading-relaxed">
                      <span className="font-medium">{insight.title}:</span> {insight.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default ProblemSolvingSection;
